import {registerCountdown} from './raith-countdown.js';
import {readCountdownContext} from './raith-countdown-context.js';

// One merchant-owned market schedule is shared with the product page. This
// consumer only presents content in the native drawer; it never changes a cart.
const one = selector => {
  const nodes = document.querySelectorAll(selector);
  return nodes.length === 1 ? nodes[0] : null;
};
const nativeImages = new WeakMap();
const imageAttributes = ['src', 'srcset', 'alt'];
function restoreImage(image) {
  const original = nativeImages.get(image);
  if (!original) return;
  for (const name of imageAttributes) {
    if (original[name] === null) image.removeAttribute(name);
    else image.setAttribute(name, original[name]);
  }
  nativeImages.delete(image);
}
function packet(selector) {
  const node = one(selector);
  if (!node || node.textContent.length > 50000) return null;
  try {
    const data = JSON.parse(node.textContent), c = data.context, s = window.Shopify;
    return data.version === 1 && c?.shop === 'raithclothing.myshopify.com' && typeof c.themeId === 'string' && /^[1-9]\d*$/.test(c.themeId)
      && ['main', 'unpublished'].includes(c.role) && s?.shop === c.shop && String(s.theme?.id) === c.themeId && s.theme.role === c.role
      && s.country === c.country && s.currency?.active === c.currency && s.locale === c.locale
      && document.documentElement.lang === c.locale ? data : null;
  } catch { return null; }
}
function message(drawer, data) {
  const host = drawer.querySelector('[data-raith-native-cart-message]');
  if (!host) return;
  host.replaceChildren(); host.hidden = true;
  const m = data?.message;
  if (!m || typeof m.text !== 'string' || !m.text.trim()) return;
  const text = document.createElement('span'); text.textContent = m.text; host.append(text);
  try {
    const url = new URL(m.linkUrl, location.origin);
    if (m.linkUrl && m.linkLabel && url.origin === location.origin && url.protocol === 'https:') {
      const link = document.createElement('a'); link.href = url.href; link.textContent = m.linkLabel; host.append(link);
    }
  } catch {}
  host.hidden = false;
}
function countdown(drawer) {
  const host = drawer.querySelector('[data-raith-native-cart-countdown]');
  if (!host) return;
  let market; try { market = readCountdownContext(window); } catch {}
  // A warehouse deadline is not a promised delivery date. Service-assessed
  // delivery stays hidden until a real eligibility service is connected.
  if (!market || market.config.mode !== 'order_cutoff') {host.replaceChildren(); host.hidden = true; return;}
  const config = JSON.stringify({...market.config, active:true, eligible:false});
  if (host.dataset.timerConfig === config && host.querySelector('raith-countdown')) return;
  host.replaceChildren(); host.dataset.timerConfig = config;
  registerCountdown(window);
  const timer = document.createElement('raith-countdown'); timer.hidden = true;
  const script = document.createElement('script'); script.type = 'application/json'; script.setAttribute('data-countdown-config',''); script.textContent = config;
  const line = document.createElement('span'); line.className = 'raith-countdown__line'; line.setAttribute('aria-live','off');
  for (const part of ['before','time','after']) {
    const node = document.createElement(part === 'time' ? 'time' : 'span'); node.setAttribute(`data-countdown-${part}`,''); line.append(node);
  }
  timer.append(script,line); host.append(timer); host.hidden = false;
}
function images(drawer) {
  const data = packet('script[data-raith-cart-images-config]');
  for (const line of drawer.querySelectorAll('[data-raith-native-cart-item]')) {
    const image = line.querySelector('img[data-native-cart-image]');
    if (!image) continue;
    const matches = data?.assignments?.filter(x => x.productId === line.dataset.productId) || [];
    const chosen = data?.enabled === true && matches.length === 1 && matches[0].allVariants === true ? matches[0].image : null;
    if (!chosen?.url) { restoreImage(image); continue; }
    try {
      const url = new URL(chosen.url, location.origin);
      if (url.protocol !== 'https:' || !(url.hostname === 'cdn.shopify.com' || url.origin === location.origin && url.pathname.startsWith('/cdn/shop/'))) { restoreImage(image); continue; }
      if (!nativeImages.has(image)) nativeImages.set(image, Object.fromEntries(imageAttributes.map(name => [name, image.getAttribute(name)])));
      const alt = chosen.alt || nativeImages.get(image).alt;
      image.removeAttribute('srcset'); image.src = url.href;
      if (alt === null) image.removeAttribute('alt'); else image.setAttribute('alt', alt);
    } catch { restoreImage(image); }
  }
}
function refresh() {
  const drawer = one('#cart-drawer'); if (!drawer) return;
  message(drawer,packet('script[data-raith-cart-content-config]')); countdown(drawer); images(drawer);
}
document.addEventListener('cart-drawer:refreshed',refresh);
document.addEventListener('dialog:before-show',event => {if (event.target.id === 'cart-drawer') refresh();}, {capture:true});
for (const event of ['shopify:section:load','shopify:section:unload','shopify:section:reorder']) document.addEventListener(event,refresh);
if (window.Shopify?.designMode) {
  let selectedSection = null;
  let selectionVersion = 0;
  const closingDrawers = new WeakMap();
  const isCartSettings = section => section?.querySelector('script[data-raith-cart-content-config],script[data-raith-cart-images-config]');
  const closeProductDrawers = () => Promise.all(Array.from(document.querySelectorAll('vl-size-drawer[open]'), drawer => drawer.hide(false)));
  const showCartSettings = async section => {
    const version = ++selectionVersion;
    const drawer = one('#cart-drawer');
    const isCurrent = () => version === selectionVersion && section.isConnected && section.id === selectedSection && drawer?.isConnected && drawer === one('#cart-drawer');
    await Promise.all([closingDrawers.get(drawer), closeProductDrawers()]);
    if (!isCurrent()) return;
    if (drawer.open && drawer.style.display === 'none') await drawer.hide();
    if (!isCurrent()) return;
    refresh();
    drawer.show(false);
  };
  document.addEventListener('shopify:section:select', event => {
    if (event.target.matches('.shopify-section--cart-drawer')) {
      selectedSection = event.target.id;
      showCartSettings(event.target);
      return;
    }
    if (!isCartSettings(event.target)) return;
    selectedSection = event.target.id;
    showCartSettings(event.target);
  });
  document.addEventListener('shopify:block:select', event => {
    const section = event.target.closest('.shopify-section');
    if (!isCartSettings(section)) return;
    selectedSection = section.id;
    showCartSettings(section);
  });
  document.addEventListener('shopify:section:deselect', event => {
    if (event.target.id !== selectedSection) return;
    selectedSection = null;
    selectionVersion += 1;
    queueMicrotask(() => {
      if (!selectedSection) one('#cart-drawer')?.hide();
    });
  });
  document.addEventListener('shopify:section:load', event => {
    if (event.target.id === selectedSection && isCartSettings(event.target)) showCartSettings(event.target);
    else if (event.target.matches('.shopify-section--cart-drawer')) {
      const section = document.getElementById(selectedSection);
      if (isCartSettings(section)) showCartSettings(section);
    }
  });
  document.addEventListener('dialog:before-show', event => {
    if (event.target.id === 'cart-drawer') closeProductDrawers();
  }, {capture:true});
  document.addEventListener('dialog:before-hide', event => {
    if (event.target.id !== 'cart-drawer') return;
    closingDrawers.set(event.target, new Promise(resolve => event.target.addEventListener('dialog:after-hide', resolve, {once:true})));
  }, {capture:true});
}
window.addEventListener('pageshow',refresh);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',refresh,{once:true}); else refresh();

